// Makalah utilities for generating outline and managing content
import linkupService from '../../services/linkupService'
import { MODEL_MODES } from '../../constants/agentConfig'

// Generate makalah outline based on query/topic using LLM
export const generateMakalahOutline = async (topic, llmService) => {
  let academicContext = ''
  let academicSources = []
  
  try {
    // Lakukan pencarian untuk mendapatkan konteks akademik yang relevan
    console.log(`🔍 Mencari konteks akademik untuk topik makalah: ${topic}`);
    const searchResults = await linkupService.getScientificInfo(`${topic} academic research paper structure methodology`);
    
    if (searchResults && searchResults.sources && searchResults.sources.length > 0) {
      academicSources = searchResults.sources.slice(0, 30); // Simpan 30 sumber untuk makalah yang lebih komprehensif
      const topSources = academicSources.slice(0, 8);
      academicContext = `\n\nKonteks Referensi Akademik:\n${topSources.map((source, index) => 
        `${index + 1}. ${source.title}\n   ${source.snippet}`
      ).join('\n')}`;
      console.log(`✅ Ditemukan ${academicSources.length} sumber akademik untuk outline makalah`);
    }
  } catch (error) {
    console.warn('❌ Error saat mencari konteks akademik:', error.message);
  }
  
  const prompt = `Buatkan OUTLINE (struktur poin-poin) makalah akademik untuk topik "${topic}" dalam format JSON dengan struktur berikut:
  {
    "sections": [
      {
        "id": "abstrak",
        "title": "Abstrak",
        "description": "Deskripsi singkat isi bagian",
        "minWords": 250,
        "isEditable": true,
        "subsections": ["Latar Belakang", "Tujuan", "Metode", "Hasil", "Kesimpulan"]
      }
    ]
  }
  
  PENTING: Ini adalah OUTLINE (kerangka/struktur) makalah akademik, BUKAN konten lengkap!
  
  WAJIB SERTAKAN BAGIAN-BAGIAN BERIKUT (TIDAK BOLEH DIHILANGKAN):
  1. Abstrak
  2. Pendahuluan (Latar Belakang, Rumusan Masalah, Tujuan, Manfaat)
  3. Tinjauan Pustaka/Landasan Teori
  4. Metodologi Penelitian
  5. Pembahasan/Analisis (bisa dibagi menjadi beberapa sub-bagian)
  6. Kesimpulan dan Saran
  7. Daftar Pustaka (id: "daftar_pustaka")
  
  Pedoman:
  - Ikuti struktur makalah akademik yang sesuai standar penelitian
  - Berikan ID unik untuk setiap bagian (contoh: abstrak, pendahuluan, tinjauan_pustaka, metodologi, pembahasan_1, kesimpulan, daftar_pustaka)
  - Sertakan subsections (sub-bagian) untuk setiap bagian utama
  - Description berisi penjelasan singkat apa yang akan dibahas di bagian tersebut
  - Target total kata: 10000-15000 kata
  - Sesuaikan dengan topik penelitian: "${topic}"
  - PASTIKAN Daftar Pustaka SELALU ada di akhir outline
  - Pertimbangkan konteks akademik yang relevan jika tersedia${academicContext}
  
  Berikan HANYA JSON tanpa penjelasan tambahan:`
  
  try {
    // Use GENIUS mode for comprehensive academic writing
    const response = await llmService.sendMessage(
      [{ role: 'user', content: prompt }],
      { 
        mode: MODEL_MODES.GENIUS,
        searchType: 'web',
        stream: false,
        consumeCredits: false // Don't consume credits for outline generation
      }
    )
    const cleanedResponse = response.replace(/```json|```/g, '').trim()
    const outline = JSON.parse(cleanedResponse)
    
    if (outline && outline.sections && Array.isArray(outline.sections)) {
      // Pastikan setiap section memiliki ID dan struktur yang benar
      let processedSections = outline.sections.map((section, index) => ({
        id: section.id || `section_${index + 1}`,
        title: section.title,
        description: section.description || '',
        minWords: section.minWords || 800,
        isEditable: section.isEditable !== false,
        subsections: section.subsections || [],
        topic: topic
      }))
      
      // Validasi dan tambahkan bagian wajib jika tidak ada
      const requiredSections = [
        {
          id: 'daftar_pustaka',
          title: 'Daftar Pustaka',
          minWords: 0,
          description: 'Daftar sumber dan referensi yang digunakan dalam makalah',
          isEditable: false
        }
      ]
      
      // Periksa dan tambahkan bagian yang hilang
      requiredSections.forEach(requiredSection => {
        const exists = processedSections.some(section => section.id === requiredSection.id)
        if (!exists) {
          console.log(`⚠️ Menambahkan bagian yang hilang: ${requiredSection.title}`)
          processedSections.push({
            ...requiredSection,
            topic: topic
          })
        }
      })
      
      return {
        outline: processedSections,
        academicSources: academicSources
      }
    } else {
      throw new Error('Format outline tidak valid')
    }
  } catch (error) {
    console.warn('❌ Error generating outline with LLM, using fallback:', error.message);
    // Fallback ke outline default jika LLM gagal
    return {
      outline: getDefaultOutline(topic),
      academicSources: academicSources
    }
  }
}

// Fallback outline function
const getDefaultOutline = (topic) => {
  return [
    {
      id: 'abstrak',
      title: 'Abstrak',
      minWords: 250,
      description: 'Ringkasan singkat dari keseluruhan makalah',
      isEditable: true,
      subsections: ['Latar Belakang', 'Tujuan', 'Metode', 'Hasil', 'Kesimpulan'],
      topic: topic
    },
    {
      id: 'pendahuluan',
      title: 'Pendahuluan',
      minWords: 1500,
      description: 'Latar belakang, rumusan masalah, tujuan, dan manfaat penelitian',
      isEditable: true,
      subsections: ['Latar Belakang', 'Rumusan Masalah', 'Tujuan Penelitian', 'Manfaat Penelitian'],
      topic: topic
    },
    {
      id: 'tinjauan_pustaka',
      title: 'Tinjauan Pustaka',
      minWords: 2500,
      description: 'Landasan teori dan penelitian terdahulu yang relevan',
      isEditable: true,
      subsections: ['Landasan Teori', 'Penelitian Terdahulu', 'Kerangka Konseptual'],
      topic: topic
    },
    {
      id: 'metodologi',
      title: 'Metodologi Penelitian',
      minWords: 1500,
      description: 'Metode dan pendekatan yang digunakan dalam penelitian',
      isEditable: true,
      subsections: ['Jenis Penelitian', 'Populasi dan Sampel', 'Teknik Pengumpulan Data', 'Teknik Analisis Data'],
      topic: topic
    },
    {
      id: 'pembahasan_1',
      title: 'Analisis dan Pembahasan',
      minWords: 4000,
      description: 'Analisis data dan pembahasan hasil penelitian',
      isEditable: true,
      subsections: ['Deskripsi Data', 'Analisis Data', 'Interpretasi Hasil', 'Diskusi'],
      topic: topic
    },
    {
      id: 'pembahasan_2',
      title: 'Implikasi dan Temuan',
      minWords: 2000,
      description: 'Implikasi teoritis dan praktis dari temuan penelitian',
      isEditable: true,
      subsections: ['Implikasi Teoritis', 'Implikasi Praktis', 'Keterbatasan Penelitian'],
      topic: topic
    },
    {
      id: 'kesimpulan',
      title: 'Kesimpulan dan Saran',
      minWords: 1000,
      description: 'Kesimpulan penelitian dan saran untuk penelitian selanjutnya',
      isEditable: true,
      subsections: ['Kesimpulan', 'Saran untuk Penelitian Selanjutnya', 'Saran Praktis'],
      topic: topic
    },
    {
      id: 'daftar_pustaka',
      title: 'Daftar Pustaka',
      minWords: 0,
      description: 'Daftar sumber dan referensi yang digunakan dalam makalah',
      isEditable: false,
      topic: topic
    }
  ]
}

// Calculate total words from outline
export const calculateTotalWords = (outline) => {
  return outline.reduce((total, section) => total + (section.minWords || 0), 0)
}

// Validate outline structure and word count
export const validateOutline = (outline) => {
  const errors = []
  
  if (!outline || outline.length === 0) {
    errors.push('Outline tidak boleh kosong')
    return { isValid: false, errors }
  }
  
  const totalWords = calculateTotalWords(outline)
  
  if (totalWords < 10000) {
    errors.push(`Total kata terlalu sedikit: ${totalWords} (minimal 10000)`)
  }
  
  if (totalWords > 15000) {
    errors.push(`Total kata terlalu banyak: ${totalWords} (maksimal 15000)`)
  }
  
  // Check for required sections
  const requiredSections = ['abstrak', 'pendahuluan', 'tinjauan', 'metodologi', 'pembahasan', 'kesimpulan']
  const sectionTitles = outline.map(s => s.title.toLowerCase())
  
  requiredSections.forEach(required => {
    const hasSection = sectionTitles.some(title => title.includes(required))
    if (!hasSection) {
      errors.push(`Bagian '${required}' diperlukan dalam outline makalah akademik`)
    }
  })
  
  return {
    isValid: errors.length === 0,
    errors,
    totalWords
  }
}

// Format progress percentage
export const formatProgress = (current, total) => {
  if (total === 0) return 0
  return Math.round((current / total) * 100)
}

// Estimate time remaining
export const estimateTimeRemaining = (current, total, startTime) => {
  if (current === 0) return 'Menghitung...'
  
  const elapsed = Date.now() - startTime
  const avgTimePerSection = elapsed / current
  const remaining = (total - current) * avgTimePerSection
  
  const minutes = Math.ceil(remaining / 60000)
  if (minutes < 1) return 'Kurang dari 1 menit'
  if (minutes === 1) return '1 menit'
  return `${minutes} menit`
}

// Parse markdown content to extract citations and clean content
export const parseMarkdownContent = (content) => {
  if (!content) return { parsedContent: '', citations: [] }
  
  // Extract citations in format [1], [2], etc.
  const citationRegex = /\[(\d+)\]/g
  const citations = []
  let match
  
  while ((match = citationRegex.exec(content)) !== null) {
    const citationNumber = parseInt(match[1])
    if (!citations.includes(citationNumber)) {
      citations.push(citationNumber)
    }
  }
  
  // For now, return content as-is (citations will be handled in display)
  return {
    parsedContent: content,
    citations: citations.sort((a, b) => a - b)
  }
}

// Format source to APA style for academic papers
export const formatSourceToAPA = (source) => {
  if (!source) return ''
  
  const currentYear = new Date().getFullYear()
  const domain = source.url ? new URL(source.url).hostname.replace('www.', '') : 'Unknown'
  
  return `${source.title} (${currentYear}). Retrieved from ${source.url || domain}`
}

// Count words in text
export const countWords = (text) => {
  if (!text) return 0
  return text.trim().split(/\s+/).filter(word => word.length > 0).length
}